import { sanitizeEventProps, sanitizePath } from '@/shared/lib/analytics/sanitize'
import type {
  AnalyticsConsentState,
  AnalyticsEventProps,
  AnalyticsProvider,
  AnalyticsProviderKey,
} from '@/shared/lib/analytics/types'
import type { AnalyticsProviderConfigItem } from '@/shared/schemas/analyticsConfig.schema'

function signature(item: AnalyticsProviderConfigItem): string {
  return JSON.stringify(item.config)
}

function safely(run: () => void): void {
  try {
    run()
  } catch {
    // A failing vendor script must never break the app.
  }
}

export function createAnalyticsRuntime(providers: readonly AnalyticsProvider[]) {
  const byKey = new Map<AnalyticsProviderKey, AnalyticsProvider>(
    providers.map((provider) => [provider.key, provider]),
  )
  const active = new Map<AnalyticsProviderKey, string>()
  let lastPath: string | null = null

  function sync(items: readonly AnalyticsProviderConfigItem[], consent: AnalyticsConsentState): void {
    const wanted = new Map<AnalyticsProviderKey, AnalyticsProviderConfigItem>()
    for (const item of items) {
      if (byKey.has(item.key) && (!item.requires_consent || consent[item.category])) {
        wanted.set(item.key, item)
      }
    }

    for (const [key, loadedSignature] of [...active]) {
      const next = wanted.get(key)
      if (next === undefined || signature(next) !== loadedSignature) {
        const provider = byKey.get(key)
        if (provider !== undefined) {
          safely(() => provider.unload())
        }
        active.delete(key)
      }
    }

    for (const [key, item] of wanted) {
      const provider = byKey.get(key)
      if (provider === undefined || active.has(key)) {
        continue
      }
      active.set(key, signature(item))
      safely(() => {
        provider.load(item.config)
        if (lastPath !== null) {
          provider.pageview(lastPath)
        }
      })
    }
  }

  function pageview(pathname: string): void {
    const path = sanitizePath(pathname)
    lastPath = path
    for (const key of active.keys()) {
      const provider = byKey.get(key)
      if (provider !== undefined) {
        safely(() => provider.pageview(path))
      }
    }
  }

  function track(name: string, props?: AnalyticsEventProps): void {
    const clean = sanitizeEventProps(props)
    for (const key of active.keys()) {
      const provider = byKey.get(key)
      if (provider !== undefined) {
        safely(() => provider.track(name, clean))
      }
    }
  }

  function activeKeys(): AnalyticsProviderKey[] {
    return [...active.keys()]
  }

  return { sync, pageview, track, activeKeys }
}

export type AnalyticsRuntime = ReturnType<typeof createAnalyticsRuntime>
